import React, {useRef, useEffect, useState} from 'react';
import {
    Chart,
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    PieController,
    ArcElement,
    LineElement,
    LineController,
    PointElement
} from 'chart.js';
import api from "../../config/axiosConfig";
import {notification} from "antd";
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(ChartDataLabels);

Chart.register(
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    PieController,
    ArcElement,
    LineElement,
    LineController,
    PointElement
);

const Dashboard = () => {
    const [labels, setLabels] = useState([]);
    const [revenues, setRevenues] = useState([]);

    const [playerYears, setPlayerYears] = useState([]);
    const [playerYearsCount, setPlayerYearsCount] = useState([]);

    const [debts, setDebts] = useState([]);
    const [totalPlayers, setTotalPlayers] = useState(0);

    const [playerStatuses, setPlayerStatuses] = useState([]);
    const [playerStatusesCount, setPlayerStatusesCount] = useState([]);

    const [coachFullName, setCoachFullName] = useState([]);
    const [playerCount, setPlayerCount] = useState([]);

    const [registrationMonth, setRegistrationMonth] = useState([]);
    const [regPlayerCount, setRegPlayerCount] = useState([]);

    const [regData, setRegData] = useState([]);
    const [tbcPayData, setTbcPayData] = useState([]);
    const [accountPayData, setAccountPayData] = useState([]);

    const [serviceFee, setServiceFee] = useState([]);
    const [studentCount, setStudentCount] = useState([]);


    const didFetch = useRef(false);


    const fetchRevenue = async () => {
        try {
            const response = await api.get('charts/monthly-range');
            const keys = response?.data?.map(item => item.month);
            const values = response?.data?.map(item => item.revenue);
            setLabels(keys);
            setRevenues(values);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch statuses.',
                placement: 'topRight',
            });
        }
    };

    const fetchPlayerYears = async () => {
        try {
            const response = await api.get('charts/player-birth-year');
            const keys = response?.data?.map(item => item.birthYear);
            const values = response?.data?.map(item => item.playerCount);
            setPlayerYears(keys);
            setPlayerYearsCount(values);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch statuses.',
                placement: 'topRight',
            });
        }
    }

    const fetchPlayerBalance = async () => {
        try {
            const response = await api.get('charts/player-balance');
            const keys = response?.data?.map(item => item.category);
            const values = response?.data?.map(item => item.count);
            setDebts(keys);
            setTotalPlayers(values);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch statuses.',
                placement: 'topRight',
            });
        }
    };

    const fetchPlayerStatuses = async () => {
        try {
            const response = await api.get('charts/player-status');
            const keys = response?.data?.map(item => item.status);
            const values = response?.data?.map(item => item.count);
            setPlayerStatuses(keys);
            setPlayerStatusesCount(values);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch statuses.',
                placement: 'topRight',
            });
        }
    };

    const fetchCoachPlayers = async () => {
        try {
            const response = await api.get('charts/coach-player');
            const keys = response?.data?.map(item => item.coachFullName);
            const values = response?.data?.map(item => item.playerCount);
            setCoachFullName(keys);
            setPlayerCount(values);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch statuses.',
                placement: 'topRight',
            });
        }
    };

    const fetchRegisteredPlayers = async () => {
        try {
            const response = await api.get('charts/registered-player-by-month');
            const keys = response?.data?.map(item => item.registrationMonth);
            const values = response?.data?.map(item => item.playerCount);
            setRegistrationMonth(keys);
            setRegPlayerCount(values);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch registered players.',
                placement: 'topRight',
            });
        }
    };

    const fetchPaymentChannels = async () => {
        try {
            const response = await api.get('charts/payment-channels');
            const labels = response?.data?.map(item => item.regMonth);
            const tbcPayData = response?.data?.map(item => item.tbcpayCount);
            const accountPayData = response?.data?.map(item => item.accountPayCount);
            setRegData(labels)
            setTbcPayData(tbcPayData);
            setAccountPayData(accountPayData)
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch payment channels.',
                placement: 'topRight',
            });
        }
    };

    const fetchServiceFees = async () => {
        try {
            const response = await api.get('charts/player-service-fee');
            const keys = response?.data?.map(item => item.serviceFee + ' ₾');
            const values = response?.data?.map(item => item.studentCount);
            setServiceFee(keys)
            setStudentCount(values);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch payment channels.',
                placement: 'topRight',
            });
        }
    };

    useEffect(() => {
        if (!didFetch.current) {
            didFetch.current = true;
            fetchRevenue();
            fetchPlayerBalance()
            fetchPlayerStatuses()
            fetchPlayerYears()
            fetchCoachPlayers()
            fetchRegisteredPlayers()
            fetchPaymentChannels()
            fetchServiceFees()
        }
    }, []);

    // Create separate refs for each chart canvas
    const barCanvasRef = useRef(null);
    const pieCanvasRef = useRef(null);
    const totalCanvasRef = useRef(null);
    const doughnutCanvasRef = useRef(null);
    const barCanvasRef2 = useRef(null);
    const lineCanvasRef = useRef(null);
    const barCanvasRef3 = useRef(null);
    const barCanvasRef4 = useRef(null);


    // Initialize the Bar Chart (Monthly Income)
    useEffect(() => {
        // Only initialize when data is available
        if (barCanvasRef.current && labels.length > 0 && revenues.length > 0) {
            const ctxBar = barCanvasRef.current.getContext('2d');

            const barChartInstance = new Chart(ctxBar, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: [{
                        label: 'Monthly Income',
                        data: revenues,
                        backgroundColor: 'rgba(75, 192, 192, 0.2)',
                        borderColor: 'rgba(75, 192, 192, 1)',
                        borderWidth: 1
                    }]
                },
                options: {
                    scales: {
                        y: {beginAtZero: true}
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        datalabels: {
                            display: false
                        },
                        title: {
                            display: true,
                            text: 'Monthly Income Overview'
                        }
                    }
                }
            });

            // Cleanup: destroy the old chart instance when data updates
            return () => {
                barChartInstance.destroy();
            };
        }
    }, [labels, revenues]);

    useEffect(() => {
        // Only initialize when data is available
        if (barCanvasRef4.current && serviceFee.length > 0 && studentCount.length > 0) {
            const ctxBar = barCanvasRef4.current.getContext('2d');

            const barChartInstance = new Chart(ctxBar, {
                type: 'bar',
                data: {
                    labels: serviceFee,
                    datasets: [{
                        label: 'Service Fee',
                        data: studentCount,
                        backgroundColor: 'rgba(154,205,50, 0.2)',
                        borderColor: 'rgba(154,205,50, 1)',
                        borderWidth: 1
                    }]
                },
                options: {
                    scales: {
                        y: {beginAtZero: true}
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        datalabels: {
                            display: false
                        },
                        title: {
                            display: true,
                            text: 'Player Service Fee Overview'
                        }
                    }
                }
            });

            // Cleanup: destroy the old chart instance when data updates
            return () => {
                barChartInstance.destroy();
            };
        }
    }, [serviceFee, studentCount]);

    // Initialize the Pie Chart (Income by Source)
    useEffect(() => {
        if (pieCanvasRef.current) {
            const ctxPie = pieCanvasRef.current.getContext('2d');

            const pieChartInstance = new Chart(ctxPie, {
                type: 'pie',
                data: {
                    labels: debts,
                    datasets: [{
                        label: 'Income Sources',
                        data: totalPlayers,
                        backgroundColor: [
                            'rgba(154,205,50, 0.2)',
                            'rgba(255, 99, 132, 0.2)',
                            // 'rgba(54, 162, 235, 0.2)',
                        ],
                        borderColor: [
                            'rgba(154,205,50, 1)',
                            'rgba(255, 99, 132, 1)',
                            // 'rgba(54, 162, 235, 0.2)',
                        ],
                        borderWidth: 1
                    }]
                },
                options: {
                    responsive: true,
                    plugins: {
                        datalabels: {
                            color: '#000',
                            font: {
                                // weight: 'bold'
                            },
                            formatter: function (value, context) {
                                const dataArr = context.chart.data.datasets[0].data;
                                const sum = dataArr.reduce((total, curr) => total + curr, 0);
                                const percentage = ((value / sum) * 100).toFixed(1);
                                return `${value} (${percentage}%)`;
                            },
                            display: true
                        },
                        title: {
                            display: true,
                            text: 'Income by Source'
                        }
                    }
                }
            });

            // Cleanup the pie chart instance when the component unmounts or data changes
            return () => {
                pieChartInstance.destroy();
            };
        }
    }, [debts, totalPlayers]);

    // Initialize the Total Stats Chart (Total Players, Teams, Coaches)
    useEffect(() => {
        if (totalCanvasRef.current && playerYears.length > 0 && playerYearsCount.length > 0) {
            const ctxTotal = totalCanvasRef.current.getContext('2d');

            const totalChartInstance = new Chart(ctxTotal, {
                type: 'bar',
                data: {
                    labels: playerYears,
                    datasets: [{
                        label: 'Total Count',
                        data: playerYearsCount,  // Example values: adjust these as needed
                        backgroundColor: [
                            'rgba(153, 102, 255, 0.2)',  // Players
                            'rgba(255, 159, 64, 0.2)',   // Teams
                            'rgba(255, 205, 86, 0.2)'    // Coaches
                        ],
                        borderColor: [
                            'rgba(153, 102, 255, 1)',
                            'rgba(255, 159, 64, 1)',
                            'rgba(255, 205, 86, 1)'
                        ],
                        borderWidth: 1
                    }]
                },
                options: {
                    scales: {
                        y: {
                            beginAtZero: true,
                            title: {
                                display: true,
                                text: 'Count'
                            }
                        },
                        x: {
                            title: {
                                display: true,
                                text: 'Year'
                            }
                        }
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        title: {
                            display: true,
                            text: 'Age Overview'
                        },
                        legend: {
                            display: false
                        }
                    }
                }
            });

            // Cleanup the total chart instance when the component unmounts or data changes
            return () => {
                totalChartInstance.destroy();
            };
        }
    }, [playerYears, playerYearsCount]);

    // Initialize the Doughnut Chart (Monthly Income)
    useEffect(() => {
        if (doughnutCanvasRef.current && playerStatuses.length > 0 && playerStatusesCount.length > 0) {
            const ctxDoughnut = doughnutCanvasRef.current.getContext('2d');

            const doughnutChartInstance = new Chart(ctxDoughnut, {
                type: 'doughnut',
                data: {
                    labels: playerStatuses,
                    datasets: [{
                        label: 'Total',
                        data: playerStatusesCount,
                        backgroundColor: [
                            'rgba(255, 99, 132, 0.2)',
                            'rgba(75, 192, 192, 0.2)',
                            'rgba(255, 206, 86, 0.2)',
                        ],
                        borderColor: [
                            'rgba(255, 99, 132, 1)',
                            'rgba(75, 192, 192, 1)',
                            'rgba(255, 206, 86, 1)',
                        ],
                        borderWidth: 1
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        datalabels: {
                            // If you don't want to show labels, leave this disabled.
                            display: true
                        },
                        title: {
                            display: true,
                            text: 'Players by Status'
                        }
                    }
                }
            });

            // Cleanup the doughnut chart instance when data updates
            return () => {
                doughnutChartInstance.destroy();
            };
        }
    }, [playerStatuses, playerStatusesCount]);

    // Initialize the Bar Chart (Coach Players)
    useEffect(() => {
        // Only initialize when data is available
        if (barCanvasRef2.current && coachFullName.length > 0 && playerCount.length > 0) {
            const ctxBar = barCanvasRef2.current.getContext('2d');

            const barChartInstance = new Chart(ctxBar, {
                type: 'bar',
                data: {
                    labels: coachFullName,
                    datasets: [{
                        label: 'Coach Players',
                        data: playerCount,
                        backgroundColor: 'rgba(255, 159, 64, 0.2)',
                        borderColor: 'rgba(255, 159, 64, 1)',
                        borderWidth: 1
                    }]
                },
                options: {
                    scales: {
                        y: {beginAtZero: true}
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        datalabels: {
                            display: false
                        },
                        title: {
                            display: true,
                            text: 'Coach Players Overview'
                        }
                    }
                }
            });

            // Cleanup: destroy the old chart instance when data updates
            return () => {
                barChartInstance.destroy();
            };
        }
    }, [coachFullName, playerCount]);

    // Initialize the Line Chart (Registered Players)
    useEffect(() => {
        // Only initialize when data is available
        if (lineCanvasRef.current && registrationMonth.length > 0 && regPlayerCount.length > 0) {
            const ctxLine = lineCanvasRef.current.getContext('2d');

            const lineChartInstance = new Chart(ctxLine, {
                type: 'line',
                data: {
                    labels: registrationMonth,
                    datasets: [{
                        label: 'Registered Players',
                        data: regPlayerCount,
                        backgroundColor: 'rgba(153, 102, 255, 0.2)',
                        borderColor: 'rgba(153, 102, 255, 1)',
                        borderWidth: 1
                    }]
                },
                options: {
                    scales: {
                        y: {beginAtZero: true}
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        datalabels: {
                            display: false
                        },
                        title: {
                            display: true,
                            text: 'Registered Players Overview'
                        }
                    }
                }
            });

            // Cleanup: destroy the old chart instance when data updates
            return () => {
                lineChartInstance.destroy();
            };
        }
    }, [registrationMonth, regPlayerCount]);

    // Initialize the Bar Chart (Payment Channels)
    useEffect(() => {
        if (barCanvasRef3.current && regData.length > 0) {
            const ctx = barCanvasRef3.current.getContext('2d');

            // Extract labels and dataset values

            const barChartInstance = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: regData,
                    datasets: [
                        {
                            label: 'TBC Pay Count',
                            data: tbcPayData,
                            backgroundColor: 'rgba(54, 162, 235, 0.2)',
                            borderColor: 'rgba(54, 162, 235, 1)',
                            borderWidth: 1
                        },
                        {
                            label: 'Account Pay Count',
                            data: accountPayData,
                            backgroundColor: 'rgba(255, 206, 86, 0.2)',
                            borderColor: 'rgba(255, 206, 86, 1)',
                            borderWidth: 1
                        }
                    ]
                },
                options: {
                    scales: {
                        y: {
                            beginAtZero: true
                        }
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        title: {
                            display: true,
                            text: 'Payment channels Overview'
                        },
                    }
                }
            });

            // Cleanup: destroy previous instance on component unmount or data update
            return () => {
                barChartInstance.destroy();
            };
        }
    }, [regData, tbcPayData, accountPayData]);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 50px'
        }}>
            {/* Bar Chart Container (Monthly Income) */}
            <div style={{height: '400px', width: '600px'}}>
                <canvas ref={barCanvasRef}></canvas>
            </div>


            {/* Bar Chart Container (Payment Channels) */}
            <div style={{height: '400px', width: '600px', marginTop: '50px'}}>
                <canvas ref={barCanvasRef3}></canvas>
            </div>

            {/* Bar Chart Container (Service Fees) */}
            <div style={{height: '400px', width: '600px', marginTop: '50px'}}>
                <canvas ref={barCanvasRef4}></canvas>
            </div>

            {/* Pie Chart Container (Income by Source) */}
            <div style={{height: '400px', width: '600px', marginTop: '50px'}}>
                <canvas ref={pieCanvasRef}></canvas>
            </div>

            {/* Doughnut Chart Container */}
            <div style={{height: '400px', width: '600px', marginTop: '50px'}}>
                <canvas ref={doughnutCanvasRef}></canvas>
            </div>

            {/* Bar Chart Container (Coach Players) */}
            <div style={{height: '400px', width: '600px', marginTop: '50px'}}>
                <canvas ref={barCanvasRef2}></canvas>
            </div>

            {/* Line Chart Container (Registered Players) */}
            <div style={{height: '400px', width: '600px', marginTop: '50px'}}>
                <canvas ref={lineCanvasRef}></canvas>
            </div>

            {/* Total Stats Chart Container (Players, Teams, Coaches) */}
            <div style={{height: '400px', width: '600px', marginTop: '50px'}}>
                <canvas ref={totalCanvasRef}></canvas>
            </div>


        </div>
    );
}

export default Dashboard;
